import {
  Box,
  ListItemButton,
  ListItemText,
  Stack,
  styled,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { DynamicIcon } from 'src/components';
import { NavItem } from 'src/components/Navbar/navbar.styles';
import { NavigationDataItem } from 'src/components/Navbar/use-navigation-items';

interface NavigationItemProps {
  item: NavigationDataItem;
  onClick?: (e: React.MouseEvent) => void;
  isActive: boolean;
  leftAdornment?: React.ReactNode;
  rightAdornment?: React.ReactNode;
  sx?: SxProps<Theme>;
}

const NavigationItemText = styled(Typography)(() => ({
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  fontWeight: '700',
  transition: 'inherit',
  color: 'inherit',
}));

const RIGHT_ADORNMENT_CLASS = 'right-adornment';

export const NavigationItem = ({
  item,
  onClick,
  isActive,
  leftAdornment,
  rightAdornment,
  sx,
}: NavigationItemProps) => {
  return (
    <NavItem
      active={isActive}
      sx={{
        position: 'relative',
        // when the NavItem is hovered, the container with class "right-adornment" becomes visible.
        // avoids unnecessary JS handling and lets css to manage everything smoothly
        [`&:hover .${RIGHT_ADORNMENT_CLASS}`]: { opacity: 1 },
        ...sx,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
        <ListItemButton
          sx={{
            flex: '1 1 auto',
            py: (theme) => theme.spacing(0),
          }}
          onClick={onClick}
        >
          {leftAdornment}

          <DynamicIcon
            name={item.iconData.name}
            library={item.iconData.library}
            filled={item.iconData.filled}
            sx={{ marginRight: '0.625rem' }}
          />

          <ListItemText primary={<NavigationItemText>{item.title}</NavigationItemText>} />

          {!!item.badgeContent && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: (theme) => theme.palette.custom.OV.sys.onPrimary,
                fontSize: '10px',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                backgroundColor: (theme) => theme.palette.custom.OV.sys.secondary,
              }}
            >
              {item.badgeContent}
            </Box>
          )}
        </ListItemButton>

        {rightAdornment ? (
          <Box
            className={RIGHT_ADORNMENT_CLASS}
            sx={{
              opacity: 0,
              transition: 'opacity 0.8s',
            }}
          >
            {rightAdornment}
          </Box>
        ) : null}
      </Stack>
    </NavItem>
  );
};
